<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Modelos de Documentos</h5>
      <b-button
        v-if="hasPermission('adicionar_documentos')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-file-earmark-plus mr-2" title="Novo Documento"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.titulo"
        placeholder="Filtrar por Título"
        class="form-control mr-2"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center justify-content-center"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('id')">
              # {{ arrow('id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('titulo')">
              Título {{ arrow('titulo') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('created_at')">
              Criado em {{ arrow('created_at') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('updated_at')">
              Atualizado em {{ arrow('updated_at') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="doc in documentos" :key="doc.id">
          <td>{{ doc.id }}</td>
          <td>{{ doc.titulo }}</td>
          <td>{{ formatDate(doc.created_at) }}</td>
          <td>{{ formatDate(doc.updated_at) }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_documentos')"
              class="bi bi-eye-fill text-primary mx-2"
              title="Detalhar"
              @click="detalhar(doc.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_documentos')"
              class="bi bi-pencil-fill text-warning mx-2"
              title="Editar"
              @click="editar(doc.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_documentos')"
              :class="[
                'bi bi-trash-fill mx-2',
                doc.em_uso ? 'text-secondary disabled' : 'text-danger'
              ]"
              :title="doc.em_uso
                ? 'Em uso pelo sistema (não pode ser excluido)'
                : 'Excluir'"
              @click="!doc.em_uso && excluir(doc.id)"
              style="cursor: pointer;"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import axios from '@/services/axios'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

const router = useRouter()
const { fetchPermissions, hasPermission } = usePermissions()

const {
  items: documentos,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort: changeSort,
  arrow
} = useSortableTable(
  `/configuracoes/documentos`,
  { key: 'created_at', order: 'desc' }
)

const pageOptions = ref([10, 15, 20, 25, 50])

const startRow = computed(() => (page.value - 1) * limit.value + 1)
const endRow = computed(() => Math.min(page.value * limit.value, totalItems.value))

function formatDate(dt) {
  return dt ? new Date(dt).toLocaleDateString('pt-BR') : ''
}

function filtrar() {
  page.value = 1
  load()
}
function limparFiltros() {
  filters.value.titulo = ''
  page.value = 1
  load()
}
function changeLimit() {
  page.value = 1
  load()
}

function irParaCadastro() {
  router.push('/configuracoes/documentos/novo')
}
function detalhar(id) {
  router.push(`/configuracoes/documentos/${id}/detalhar`)
}
function editar(id) {
  router.push(`/configuracoes/documentos/${id}`)
}
async function excluir(id) {
  if (confirm('Deseja excluir este documento?')) {
    await axios.delete(`/configuracoes/documentos/${id}`)
    load()
  }
}

watch(page, () => {
  load()
})

watch(limit, () => {
  page.value = 1
  load()
})

onMounted(async () => {
  await fetchPermissions()
  filters.titulo = ''
  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
.table-hover tr:hover { background-color: var(--bs-light); }
</style>
